// The four core plan surfaces (hf-e2-design-shell-ej8.5). Phase-1 exit demo.
//
// Every one is a PURE component over the view models in `planData.ts`. They know
// nothing about where data comes from, which is what lets Epic 3 swap fixtures
// for reactive Convex queries without touching them.
//
// COPY COMES FROM `@herefor/product-language`, ALWAYS. Nothing here composes a
// user-facing sentence; the live copy guard enforces it, and the tone rules are
// somebody else's considered work rather than mine to improvise.
//
// Responsive behaviour is CSS-only, in `planStyles.ts`: 360 / 768 / 1280. No
// JavaScript width branching, so the surfaces render identically in a test as on
// a phone and there is no breakpoint state to get out of sync.

import { Card, StateChip, EmptyState, type StateChipState } from "@herefor/design-system";
import {
  AVAILABILITY_MESSAGES,
  CANONICAL_VOCABULARY,
  COMMITMENT_MESSAGES,
  EMPTY_STATES,
  PROXY_COMMITMENT_MESSAGES,
  STATUS_CONTEXT,
  type UserVisibleState,
} from "@herefor/product-language";
import { Fragment, type ReactNode } from "react";

import {
  formatAgendaDayHeading,
  type AvailabilityRowView,
  type EventView,
  type NeedView,
  type ResponsibleView,
} from "./planData";

/**
 * User-visible state → the chip's state token.
 *
 * Exhaustive, so a new state cannot fall through to a default colour. The chip
 * takes an identifier and its words separately: the design system deliberately
 * cannot produce copy.
 */
const CHIP_STATE: Record<UserVisibleState, StateChipState> = {
  needsSomeone: "needsSomeone",
  covered: "covered",
  pleaseRecheck: "pleaseRecheck",
  done: "done",
  cancelled: "cancelled",
};

function NeedStatus({ need }: { readonly need: NeedView }) {
  return (
    <p className="plan-need__status">
      <StateChip label={need.stateLabel} state={CHIP_STATE[need.stateKey]} />
      {need.counts === null ? null : (
        // NED-012: the shortfall reads as a count of people, next to the state,
        // so "Needs someone · 1 of 2 people confirmed" is one thought.
        <span className="plan-need__count">
          {" · "}
          {STATUS_CONTEXT.coverageCount(need.counts.confirmed, need.counts.required)}
        </span>
      )}
      {need.isPast ? (
        // Plan §10.2: Past is a display treatment ALONGSIDE the state, never
        // instead of it. A past need that still needs somebody is both.
        <StateChip label={STATUS_CONTEXT.past} state="past" />
      ) : null}
    </p>
  );
}

function Responsible({ people }: { readonly people: readonly ResponsibleView[] }) {
  if (people.length === 0) return null;

  return (
    <ul className="plan-need__responsible">
      {people.map((person) => (
        <li
          key={String(person.personId)}
          title={
            person.recordedByName === null
              ? undefined
              : PROXY_COMMITMENT_MESSAGES.provenance(
                  person.name,
                  person.recordedByName,
                  person.agreementSource ?? "",
                )
          }
        >
          {person.recordedByName === null ? (
            <span className="plan-need__person">{person.name}</span>
          ) : (
            // COM-009 / invariant 19: a proxy record discloses its recorder and
            // how the person agreed as one indivisible statement. `title` is the
            // compact week-view tooltip; the same text remains visible in detail.
            <span className="plan-need__proxy">
              {PROXY_COMMITMENT_MESSAGES.provenance(
                person.name,
                person.recordedByName,
                person.agreementSource ?? "",
              )}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

interface WhenLineProps {
  readonly className: string;
  readonly dateClassName: string;
  readonly dateLabel: string | null;
  readonly timeClassName: string;
  readonly timeLabel: string | null;
  readonly placeClassName?: string;
  readonly placeLabel?: string | null;
}

/**
 * One readable date/time/place statement.
 *
 * The middle dots are text, not layout: copied text and a screen reader therefore
 * preserve the same boundaries a sighted person sees. Separators are inserted only
 * between present parts, so absent optional parts can never leave one dangling.
 */
function WhenLine({
  className,
  dateClassName,
  dateLabel,
  timeClassName,
  timeLabel,
  placeClassName,
  placeLabel,
}: WhenLineProps) {
  const parts = [
    dateLabel === null
      ? null
      : { className: dateClassName, key: "date", text: formatAgendaDayHeading(dateLabel) },
    timeLabel === null ? null : { className: timeClassName, key: "time", text: timeLabel },
    placeClassName === undefined || placeLabel === null || placeLabel === undefined
      ? null
      : { className: placeClassName, key: "place", text: placeLabel },
  ].filter(
    (part): part is { readonly className: string; readonly key: string; readonly text: string } =>
      part !== null,
  );

  return (
    <p className={className}>
      {parts.map((part, index) => (
        <Fragment key={part.key}>
          {index === 0 ? null : " · "}
          <span className={part.className}>{part.text}</span>
        </Fragment>
      ))}
    </p>
  );
}

function NeedRow({
  need,
  renderAction,
}: {
  readonly need: NeedView;
  readonly renderAction?: (need: NeedView) => ReactNode;
}) {
  return (
    <li className="plan-need" data-state={need.stateKey}>
      <p className="plan-need__label">
        {/* Linked when the record is live, so a member can reach the need and offer
            to help (bead hf-e6-commitments-coverage-udt.2). The fixture-backed
            surfaces have no route to point at, and render the label plainly. */}
        {need.href === undefined ? need.label : <a href={need.href}>{need.label}</a>}
      </p>
      {/* The NEED's window, never the event's (NED-002, NED-010, EVT-005). */}
      <WhenLine
        className="plan-need__window"
        dateClassName="plan-need__date"
        dateLabel={need.dateLabel}
        timeClassName="plan-need__time"
        timeLabel={need.windowLabel}
      />
      <NeedStatus need={need} />
      <Responsible people={need.responsible} />
      {renderAction?.(need) ?? null}
    </li>
  );
}

/* ------------------------------------------------------------ plan week --- */

export interface PlanWeekProps {
  readonly heading: string;
  readonly events: readonly EventView[];
  /**
   * Needs belonging to no event (bead hf-qyq).
   *
   * They sit in the same dated list as the events, whatever their coverage, because
   * this list is the plan's "what is happening" and a need with its own window is a
   * thing that is happening. Their previous only home was the attention list, which
   * by design keeps just the states that still want a person — so covering one made
   * it unreachable from the app while it went on existing.
   */
  readonly standaloneNeeds?: readonly NeedView[];
  /** Capability-gated action supplied by the authenticated route shell. */
  readonly renderNeedAction?: (need: NeedView) => ReactNode;
}

/**
 * Interleave standalone needs into the events, without reordering the events.
 *
 * The events arrive in the server's own chronological order and keep it exactly —
 * including any undated draft, which never wins a comparison and therefore never
 * moves. Each need is placed before the first event that starts after it, and any
 * left over go at the end. UX-006: the agenda reads in date order.
 */
type PlanWeekRow = { readonly key: string } & (
  | { readonly kind: "event"; readonly event: EventView }
  | { readonly kind: "need"; readonly need: NeedView }
);

function planWeekRows(
  events: readonly EventView[],
  standaloneNeeds: readonly NeedView[],
): readonly PlanWeekRow[] {
  const rows: PlanWeekRow[] = [];
  let pending = 0;

  for (const event of events) {
    while (
      pending < standaloneNeeds.length &&
      event.startsAt !== null &&
      (standaloneNeeds[pending]?.startsAt ?? 0) <= event.startsAt
    ) {
      const need = standaloneNeeds[pending];
      pending += 1;
      if (need !== undefined) rows.push({ key: `need:${need.id}`, kind: "need", need });
    }
    rows.push({ key: `event:${event.id}`, kind: "event", event });
  }

  for (const need of standaloneNeeds.slice(pending)) {
    rows.push({ key: `need:${need.id}`, kind: "need", need });
  }

  return rows;
}

/** One local day of the agenda, with the rows that fall on it. */
interface AgendaDay {
  /** `YYYY-MM-DD` in the CIRCLE's zone, or null for a draft with no date yet. */
  readonly localDate: string | null;
  readonly heading: string;
  readonly rows: readonly PlanWeekRow[];
}

/**
 * Cut the chronological row list into local-day groups (UX-006, plan §14.4).
 *
 * ORDER IS NOT RECOMPUTED HERE, and that is deliberate rather than lazy. `planWeekRows`
 * has already interleaved the standalone needs into the events chronologically, and it
 * keeps the events in exactly the order they arrived — which in production is the
 * server's `by_circle_start` index, and which the existing "without disturbing the
 * events" test pins on purpose. An undated draft holds the position the server gave
 * it, because it never wins a comparison and so must never lose one either.
 *
 * So this inserts BOUNDARIES into an ordered list and nothing more. One sort, upstream,
 * means the grouping can never disagree with the ordering — whereas a second sort here
 * would be a second opinion about the same question, and the two would drift.
 *
 * THE DAY COMES FROM `dateLabel`, WHICH IS A `LocalDate` THE SERVER'S ZONE PRODUCED.
 * Bucketing by `startsAt` here would re-derive the day from an instant in whatever
 * zone the reader's browser happens to be in, and a 23:30 need would jump a heading
 * for anybody outside the circle's zone (invariant 9).
 *
 * UNDATED DRAFTS GET THEIR OWN TRAILING GROUP rather than being hidden or forced
 * under a day they are not on. An event whose time the clinic has not confirmed is a
 * normal record (plan §9.5, "start/end where known"), and the agenda is where an
 * organiser would look for it.
 */
function agendaDays(rows: readonly PlanWeekRow[]): readonly AgendaDay[] {
  const days: AgendaDay[] = [];

  for (const row of rows) {
    const localDate = row.kind === "need" ? row.need.dateLabel : row.event.dateLabel;
    const last = days[days.length - 1];

    if (last?.localDate === localDate) {
      days[days.length - 1] = { ...last, rows: [...last.rows, row] };
      continue;
    }

    days.push({
      localDate,
      heading:
        localDate === null ? CANONICAL_VOCABULARY.noDateYet : formatAgendaDayHeading(localDate),
      rows: [row],
    });
  }

  return days;
}

/** Plan home: what is happening, and which needs still want somebody. */
export function PlanWeek({
  heading,
  events,
  standaloneNeeds = [],
  renderNeedAction,
}: PlanWeekProps) {
  const days = agendaDays(planWeekRows(events, standaloneNeeds));

  return (
    <section aria-labelledby="plan-week-heading" className="plan-surface plan-week">
      <h1 id="plan-week-heading">{heading}</h1>

      {days.length === 0 ? (
        <EmptyState
          description={EMPTY_STATES.nothingPlannedToday.body}
          title={EMPTY_STATES.nothingPlannedToday.title}
        />
      ) : (
        <ol className="plan-week__days">
          {days.map((day) => (
            <li
              className="plan-week__day"
              data-agenda-day={day.localDate ?? "undated"}
              key={day.localDate ?? "undated"}
            >
              {/* A real heading, not a styled row. It is what lets somebody move
                  between days with a screen reader's heading list rather than by
                  reading every card, and it is the §14.4 layout as specified. */}
              <h2 className="plan-week__day-heading">{day.heading}</h2>
              <ol className="plan-week__list">
                {day.rows.map((row) =>
                  row.kind === "need" ? (
                    <li data-standalone-need={row.need.id} key={row.key}>
                      <Card>
                        {/* The same row the linked needs use, so a need reads
                            identically wherever it appears and no second
                            presentation can drift. */}
                        <ul className="plan-event__needs">
                          <NeedRow
                            need={row.need}
                            {...(renderNeedAction === undefined
                              ? {}
                              : { renderAction: renderNeedAction })}
                          />
                        </ul>
                      </Card>
                    </li>
                  ) : (
                    <PlanWeekEvent
                      event={row.event}
                      key={row.key}
                      {...(renderNeedAction === undefined ? {} : { renderNeedAction })}
                    />
                  ),
                )}
              </ol>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function PlanWeekEvent({
  event,
  renderNeedAction,
}: {
  readonly event: EventView;
  readonly renderNeedAction?: (need: NeedView) => ReactNode;
}) {
  return (
    <li>
      <Card>
        <h3 className="plan-event__title">
          {event.href === undefined ? event.title : <a href={event.href}>{event.title}</a>}
        </h3>
        {event.stateKey === null || event.stateLabel === null ? null : (
          <p className="plan-event__state">
            <StateChip label={event.stateLabel} state={CHIP_STATE[event.stateKey]} />
          </p>
        )}
        <WhenLine
          className="plan-event__when"
          dateClassName="plan-event__date"
          dateLabel={event.dateLabel}
          placeClassName="plan-event__place"
          placeLabel={event.locationLabel}
          timeClassName="plan-event__time"
          timeLabel={event.windowLabel}
        />

        {event.needs.length === 0 ? null : (
          <ul className="plan-event__needs">
            {event.needs.map((need) => (
              <NeedRow
                key={need.id}
                need={need}
                {...(renderNeedAction === undefined ? {} : { renderAction: renderNeedAction })}
              />
            ))}
          </ul>
        )}
      </Card>
    </li>
  );
}

/* --------------------------------------------------------- event detail --- */

export interface EventDetailProps {
  readonly event: EventView;
}

/**
 * One event and the help it needs.
 *
 * The information-design point this surface exists to settle: the event window
 * and each need's window are shown as SEPARATE facts, close enough to compare.
 * A driver needed 09:15–13:00 for a 10:15 appointment must never read as 10:15.
 */
export function EventDetail({ event }: EventDetailProps) {
  return (
    <section aria-labelledby="event-detail-heading" className="plan-surface plan-event-detail">
      <h1 id="event-detail-heading">{event.title}</h1>

      {event.stateKey === null || event.stateLabel === null ? null : (
        <p className="plan-event-detail__state">
          <StateChip label={event.stateLabel} state={CHIP_STATE[event.stateKey]} />
        </p>
      )}

      <dl className="plan-event-detail__facts">
        <dt>{CANONICAL_VOCABULARY.whatsHappening}</dt>
        <dd>
          {event.dateLabel ?? ""} {event.windowLabel ?? ""}
        </dd>
        {event.locationLabel === null ? null : <dd>{event.locationLabel}</dd>}
      </dl>

      <h2 className="plan-event-detail__needs-heading">{CANONICAL_VOCABULARY.helpNeeded}</h2>
      {event.needs.length === 0 ? (
        <EmptyState
          description={EMPTY_STATES.everythingCovered.body}
          title={EMPTY_STATES.everythingCovered.title}
        />
      ) : (
        <ul className="plan-need-list">
          {event.needs.map((need) => (
            <NeedRow key={need.id} need={need} />
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------ needs attention --- */

export interface NeedsAttentionProps {
  readonly heading: string;
  readonly needs: readonly NeedView[];
}

/**
 * What wants a person, most urgent first.
 *
 * Ordering is decided in `planData.needsAttention`, not here: "Please recheck"
 * before "Needs someone", because a promise at risk is worse than a gap still to
 * fill. This surface only renders the order it is given.
 */
export function NeedsAttention({ heading, needs }: NeedsAttentionProps) {
  return (
    <section aria-labelledby="needs-attention-heading" className="plan-surface plan-attention">
      <h1 id="needs-attention-heading">{heading}</h1>

      {needs.length === 0 ? (
        <EmptyState
          description={EMPTY_STATES.everythingCovered.body}
          title={EMPTY_STATES.everythingCovered.title}
        />
      ) : (
        <ul className="plan-need-list">
          {needs.map((need) => (
            <NeedRow key={need.id} need={need} />
          ))}
        </ul>
      )}
    </section>
  );
}

/* -------------------------------------------------- availability overview --- */

export interface AvailabilityOverviewProps {
  readonly heading: string;
  readonly rows: readonly AvailabilityRowView[];
}

/**
 * Who could help, and when.
 *
 * "Could help" throughout — never "booked", "assigned" or "free" (AVL-026).
 * Somebody with nothing recorded is simply absent: unmarked means Not set, not
 * unavailable (AVL-009, invariant 13), so there is no "unavailable" row to read
 * as a refusal.
 */
export function AvailabilityOverview({ heading, rows }: AvailabilityOverviewProps) {
  return (
    <section aria-labelledby="availability-heading" className="plan-surface plan-availability">
      <h1 id="availability-heading">{heading}</h1>
      <p className="plan-availability__meaning">{AVAILABILITY_MESSAGES.meaning}</p>

      {rows.length === 0 ? (
        <EmptyState
          description={EMPTY_STATES.noOneHasAddedAvailability.body}
          title={EMPTY_STATES.noOneHasAddedAvailability.title}
        />
      ) : (
        <ul className="plan-availability__list">
          {rows.map((row) => (
            <li className="plan-availability__row" key={row.key}>
              <span className="plan-availability__person">{row.name}</span>
              <span className="plan-availability__date">{row.dateLabel}</span>
              <span className="plan-availability__time">{row.windowLabel}</span>
              <span className="plan-availability__modes">{row.helpModes.join(" · ")}</span>
              {row.recordedByName === null ? null : (
                <span className="plan-availability__proxy">
                  {COMMITMENT_MESSAGES.proxyRecorded(row.name, row.recordedByName, "")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
